import GrmMediaStreamTrackCanvas from "@/lib/gooroomee/track-generator/GrmMediaStreamTrackCanvas";
import Worker from 'worker-loader!./GrmMediaStreamTrackCanvasWorker.runner'
import GrmWorkerUtils from "@/lib/gooroomee/worker/grm-worker-utils";
import {ImageUtils} from "@/lib/gooroomee/image-process/ImageUtils";

export default class GrmMediaStreamTrackCanvasWorker extends GrmMediaStreamTrackCanvas {
    worker;
    workerProxy;

    /**
     * iOS 16 이하 및 iOS 앱 WebView에서 Worker 사용 방지
     * Worker 내부에서 document 접근 시 ReferenceError 발생하는 문제 해결
     * 
     * @see https://caniuse.com/offscreencanvas
     * - iOS 16.4+: OffscreenCanvas 지원, 하지만 Worker 내부에서 document 접근 불가
     * - iOS 17+: Safari 웹에서는 완전 지원, 하지만 앱 WebView는 여전히 제한적
     * - iOS 앱 WebView: 버전 관계없이 Worker 사용 불가
     */
    static isSupported() {
        // 1. OffscreenCanvas 미지원 브라우저 체크
        if (!globalThis.OffscreenCanvas) {
            return false;
        }
        
        // 2. iOS 앱 WebView 감지 (최우선 체크) - 강화된 감지 로직
        const isIOSWebView = (() => {
            try {
                const ua = navigator.userAgent || '';
                const isiPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
                const isIOS = /iPhone|iPad|iPod/i.test(ua) || isiPadOS;
                
                if (!isIOS) return false;
                
                // WebView 감지 조건들 (하나라도 참이면 WebView)
                
                // 1) window.webkit.messageHandlers 존재 (iOS 앱 브릿지)
                const hasWebKitBridge = typeof window !== 'undefined' && 
                                       window.webkit && 
                                       window.webkit.messageHandlers;
                if (hasWebKitBridge) return true;
                
                // 2) navigator.standalone (PWA/WebView에서 true)
                if (navigator.standalone === true) return true;
                
                // 3) display-mode: standalone (PWA/WebView)
                try {
                    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
                        return true;
                    }
                } catch (e) {}
                
                // 4) 인앱 브라우저 User Agent 패턴
                const isInApp = /(FBAN|FBAV|FB_IAB|Instagram|Line|KAKAOTALK|NAVER|DaumApps|GSA)/i.test(ua);
                if (isInApp) return true;
                
                // 5) User Agent에 Version이 없음 (일부 앱이 제거)
                const noSafariVersion = !/Version\/\d+/i.test(ua);
                if (isIOS && noSafariVersion) return true;
                
                return false;
            } catch (e) {
                return false;
            }
        })();
        
        if (isIOSWebView) {
            console.log('[GrmMediaStreamTrackCanvasWorker] iOS WebView detected - Worker not supported (document access issue in WebView Worker)');
            return false;
        }
        
        // 3. iOS 16 이하 감지 (Safari 웹)
        try {
            const ua = navigator.userAgent || '';
            
            // iPad 감지 (macOS처럼 보이지만 터치 가능)
            const isiPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
            
            // iOS 감지
            const isIOS = /iPhone|iPad|iPod/i.test(ua) || isiPadOS;
            
            // iOS가 아니면 Worker 사용 가능
            if (!isIOS) {
                return true;
            }
            
            // iOS인 경우 Safari 버전 체크
            const safariMatch = ua.match(/Version\/([\d.]+).*Safari/i);
            if (safariMatch) {
                // Major 버전만 추출 (16.6.1 → 16)
                const majorVersion = parseInt(safariMatch[1].split('.')[0]);
                
                // iOS 16 이하: Worker 내부에서 document 접근 불가
                if (!isNaN(majorVersion) && majorVersion > 0 && majorVersion <= 16) {
                    console.log(`[GrmMediaStreamTrackCanvasWorker] iOS Safari ${majorVersion} detected - Worker not supported (document access issue in Worker)`);
                    return false;
                }
                
                // iOS 17+ Safari 웹: Worker 사용 가능
                return true;
            }
            
            // Safari 버전 매치 실패 시 OS 버전으로 추가 체크
            const osMatch = ua.match(/OS ([\d_]+)/i);
            if (osMatch) {
                // Major 버전만 추출 (16_7_11 → 16)
                const osMajorVersion = parseInt(osMatch[1].split('_')[0]);
                if (!isNaN(osMajorVersion) && osMajorVersion > 0 && osMajorVersion <= 16) {
                    console.log(`[GrmMediaStreamTrackCanvasWorker] iOS ${osMajorVersion} detected - Worker not supported`);
                    return false;
                }
            }
            
            // 버전 감지 실패한 iOS: 안전하게 false 반환 (WebView일 가능성)
            if (isIOS) {
                console.log('[GrmMediaStreamTrackCanvasWorker] iOS detected but version unknown - Worker not supported (safe fallback)');
                return false;
            }
            
            return true;
        } catch (e) {
            console.warn('[GrmMediaStreamTrackCanvasWorker] Version detection failed:', e);
            // 에러 발생 시 안전하게 false 반환 (iOS일 가능성 고려)
            return false;
        }
    }

    async init() {
        await super.init();
        // this._setScreenOrientationChangeEventListener();

        // create worker
        this.worker = new Worker();
        this.workerProxy = await GrmWorkerUtils.createWorkerProxy({
            init: () => {},
            destroy: () => {},
            render: () => {},
            resize: () => {},
            resizeCanvas: () => {},
            setFaceImage: () => {},
            pipeThrough: () => {},
            setPipeOptions: () => {},
            setBlur: () => {},
            setMirror: () => {},
            setFaceDrawType: () => {},
            drawPauseImage: () => {},
            changeScreenOrientation: () => {},
        }, this.worker);

        let canvas = this.getRenderCanvas().transferControlToOffscreen();
        this._props.workerCanvas = canvas;
        await this.workerProxy.init({canvas})
    }

    // _setScreenOrientationChangeEventListener () {
    //     if(!globalThis.screen) return;
    //     let {type, angle} = screen.orientation
    //     this._props.orientation = type;
    //     this._screenChangeListener = this._screenChangeListener || ((event) => {
    //         if(this.isDestroyed()){
    //             this._unsetScreenOrientationChangeEventListener();
    //             return;
    //         }
    //         let {type, angle} = screen.orientation
    //         this._props.orientation = {type, angle};
    //         // this.workerProxy.changeScreenOrientation({type, angle});
    //     });
    //     this._unsetScreenOrientationChangeEventListener();
    //     screen.orientation.addEventListener('change', this._screenChangeListener);
    // }
    // _unsetScreenOrientationChangeEventListener () {
    //     if(!globalThis.screen) return;
    //     screen.orientation.removeEventListener('change', this._screenChangeListener);
    // }

    async destroy() {
        // this._unsetScreenOrientationChangeEventListener();
        await super.destroy();
        await this.workerProxy.destroy();
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                try{
                    this.worker.terminate();
                }finally{
                    resolve()
                }
            }, 10)
        });
    }

    async render() {
        const { video } = this._props;
        if (video.readyState < video.HAVE_ENOUGH_DATA) {
            return;
        }

        if(this.paused()){
            this.workerProxy.drawPauseImage();
            return;
        }

        const image = await createImageBitmap(video);
        const {resizeMode} = this._props;

        const iosFlip = (() => {
            // ios 최신 기기에서 가로모드로 일정 시간 경과하면 전면카메라가 portrait 로 강제 전환되는 현상.
            if(!this._props.deviceInfo.isIOS) return;
            const ot = globalThis.screen?.orientation;
            if(this._props.facingMode !== 'user' || !ot?.type.includes('landscape')){
                return;
            }
            const {track} = this._props;
            if(!track) return;
            const settings = track.getSettings();
            const cons = track.getConstraints();
            if(cons.width !== settings.height || cons.height !== settings.width){
                return;
            }
            return {type: ot.type, angle: ot.angle};
        })();

        // send worker
        this.workerProxy.render({image, resizeMode, iosFlip});

        // event trigger
        // this._triggerVideoFrameEvent({image})
    }

    pause (options) {
        super.pause({drawPauseImage: false});
        // worker render term 간격 때문에 비동기 실행. 문제없는지 추후 검토.
        setTimeout(() => {
            this.workerProxy.drawPauseImage(options);
        }, 1000/30);
    }

    async resize () {
        if(!this.workerProxy){
            super.resize.apply(this, arguments);
            return;
        }
        this.workerProxy.resize.apply(this.workerProxy, arguments);
    }
    async resizeCanvas () {
        if(!this.workerProxy){
            super.resizeCanvas.apply(this, arguments);
            return;
        }
        this.workerProxy.resizeCanvas.apply(this.workerProxy, arguments);
    }

    async _createFaceImage (img, size) {
        if(typeof img === 'string'){
            img = await (async () => {
                return new Promise((resolve, reject) => {
                    const image = new Image();
                    image.onload = () => resolve(image);
                    image.onerror = reject;
                    image.src = img;
                });
            })();
        }

        // resize
        (() => {
            size = size || 300;
            let {width, height} = ImageUtils.getSize(img);
            if(width === size) return;

            let ratio = size / width;
            
            // OffscreenCanvas 안전 사용 (방어적 프로그래밍)
            // 이 클래스가 선택되었다면 OffscreenCanvas는 반드시 존재하지만, 안전장치 추가
            if (!globalThis.OffscreenCanvas) {
                console.warn('[GrmMediaStreamTrackCanvasWorker] OffscreenCanvas not available in _createFaceImage');
                return; // resize 건너뜀
            }
            
            const cvs = new OffscreenCanvas(width*ratio, height*ratio)
            const ctx = cvs.getContext('2d');
            ctx.drawImage(img, 0, 0, ctx.canvas.width, ctx.canvas.height);
            img = cvs;
        })();

        if(
            img instanceof ImageBitmap === false
            // || img instanceof HTMLCanvasElement
            // || img instanceof OffscreenCanvas
            // || img instanceof VideoFrame
            // || img instanceof HTMLVideoElement
        ){
            img = await createImageBitmap(img);
        }

        return img;
    }

    async setFaceImage (image, size) {
        image = await this._createFaceImage(image, size);
        this.workerProxy.setFaceImage({image});
    }

    async pipeThrough (name, options) {
        this.pause();
        try{
            if(options?.faceImage){
                options.faceImage = await this._createFaceImage(options?.faceImage);
            }
            await this.workerProxy.pipeThrough.apply(this.workerProxy, arguments);
        }finally{
            this.resume();
        }
    }
    setPipeOptions () {
        this.workerProxy.setPipeOptions.apply(this.workerProxy, arguments);
    }
    setBlur () {
        this.workerProxy.setBlur.apply(this.workerProxy, arguments);
    }
    setMirror () {
        this.workerProxy.setMirror.apply(this.workerProxy, arguments);
    }
    setFaceDrawType () {
        this.workerProxy.setFaceDrawType.apply(this.workerProxy, arguments);
    }
}